import { FC, useEffect, useMemo, useState } from 'react'

import {
  DevChart,
  DevPanel,
  DevPanelMessage,
  DevPanelToggle
} from '../DevPanel'
import styles from './CpuPanel.module.scss'
import { CpuPanelProps } from './CpuPanel.types'

const MAX_LIFETIME_POINTS = 3600
const COLOR = 'rgb(251,146,60)'

const VIEW_OPTIONS = [
  { value: 'last1min', label: '1 min' },
  { value: 'lifetime', label: 'Lifetime' }
]

type View = 'last1min' | 'lifetime'

// The Worker pings the main thread every PING_INTERVAL ms.
// The main thread responds with 'pong' immediately via its onmessage handler.
// If the main thread is blocked (JS task running), the pong is delayed.
// We accumulate that excess latency over 1 second → CPU busy %.
//
// Worker → main:  'ping'  (string)
// Main   → worker: 'pong'  (string)
// Worker → main:  { type: 'cpu', usage: number }  (after each 1s window)
const WORKER_CODE = `
  const PING_INTERVAL = 20
  const WINDOW_MS = 1000
  const WARMUP = 20

  let baseline = Infinity
  let warmup = WARMUP
  let windowStart = performance.now()
  let windowBusy = 0
  let pingStart = 0

  function sendPing() {
    pingStart = performance.now()
    self.postMessage('ping')
  }

  self.addEventListener('message', e => {
    if (e.data !== 'pong') return
    const rtt = performance.now() - pingStart

    if (warmup > 0) {
      if (rtt < baseline) baseline = rtt
      warmup--
      setTimeout(sendPing, PING_INTERVAL)
      return
    }

    windowBusy += Math.max(0, rtt - baseline)

    const now = performance.now()
    if (now - windowStart >= WINDOW_MS) {
      const elapsed = now - windowStart
      const usage = Math.min(100, Math.round(windowBusy / elapsed * 100))
      self.postMessage({ type: 'cpu', usage })
      windowBusy = 0
      windowStart = now
    }

    setTimeout(sendPing, PING_INTERVAL)
  })

  sendPing()
`

const CPU_INFO =
  'Estimates main-thread busyness via ping-pong latency. ' +
  'A Web Worker pings the main thread every 20 ms; if JS is ' +
  'executing (renders, event handlers, timers, etc.) the reply ' +
  'is delayed. Accumulated excess delay over 1 s ÷ 1 s = CPU %.'

function fmt(n: number): string {
  return Math.round(n).toString()
}

export const CpuPanel: FC<CpuPanelProps> = ({ onClose }) => {
  const [history, setHistory] = useState<number[]>([])
  const [view, setView] = useState<View>('last1min')

  useEffect(() => {
    const blob = new Blob([WORKER_CODE], { type: 'application/javascript' })
    const url = URL.createObjectURL(blob)
    const worker = new Worker(url)

    worker.onmessage = (e: MessageEvent) => {
      if (e.data === 'ping') {
        // Respond immediately — delay here = main thread was blocked
        worker.postMessage('pong')
      } else if (e.data?.type === 'cpu') {
        setHistory(prev => {
          const next = [...prev, e.data.usage as number]
          return next.length > MAX_LIFETIME_POINTS
            ? next.slice(-MAX_LIFETIME_POINTS)
            : next
        })
      }
    }

    return () => {
      worker.terminate()
      URL.revokeObjectURL(url)
    }
  }, [])

  const displayedHistory = useMemo(
    () => (view === 'last1min' ? history.slice(-60) : history),
    [history, view]
  )

  const latest = history[history.length - 1]

  const avg = useMemo(() => {
    if (displayedHistory.length === 0) return null
    const sum = displayedHistory.reduce((a, b) => a + b, 0)
    return Math.round(sum / displayedHistory.length)
  }, [displayedHistory])

  return (
    <DevPanel
      title='CPU'
      info={CPU_INFO}
      defaultX={window.innerWidth - 400}
      defaultY={700}
      currentValue={
        latest != null ? (
          <span className={styles.currentValue}>
            {fmt(latest)}
            <span className={styles.currentUnit}>%</span>
          </span>
        ) : undefined
      }
      actions={
        <DevPanelToggle
          options={VIEW_OPTIONS}
          value={view}
          onChange={v => setView(v as View)}
        />
      }
      onClose={onClose}
      footer={
        latest != null ? (
          <>
            <span>Current: {fmt(latest)}%</span>
            {avg != null && <span>Avg: {fmt(avg)}%</span>}
          </>
        ) : undefined
      }
    >
      {displayedHistory.length < 2 ? (
        <DevPanelMessage>Calibrating…</DevPanelMessage>
      ) : (
        <DevChart
          data={displayedHistory}
          totalPoints={history.length}
          color={COLOR}
          unit='%'
          formatValue={fmt}
          fixedMax={100}
        />
      )}
    </DevPanel>
  )
}
